import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Button, LinearProgress, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  CloudUpload as UploadIcon,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
} from '@mui/icons-material';
import { ContentBlock } from '../../../types/blocks';
import { mediaService } from '../../../services/media.service';

interface VideoBlockEditorProps {
  block: ContentBlock;
  onChange: (block: ContentBlock) => void;
}

const VideoBlockEditor: React.FC<VideoBlockEditorProps> = ({ block, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isExternalUrl = block.content && /^https?:\/\/.+/.test(block.content);
  const isS3Key = block.content && block.content.startsWith('media/');

  useEffect(() => {
    if (isS3Key) {
      mediaService.resolveMediaUrl(block.content).then(setPreviewUrl);
    } else if (isExternalUrl) {
      setPreviewUrl(block.content);
    } else {
      setPreviewUrl('');
    }
  }, [block.content, isS3Key, isExternalUrl]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      const { uploadUrl, key } = await mediaService.presignUpload(
        file.name,
        file.type,
        file.size,
      );

      await mediaService.uploadToS3(uploadUrl, file, setUploadProgress);

      onChange({ ...block, content: key });
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const hasPreview = !!previewUrl;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Video URL (https://...) or upload a file"
          value={block.content}
          onChange={(e) => onChange({ ...block, content: e.target.value })}
          disabled={uploading}
        />
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
        >
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/webm"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>
      {uploading && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}
      {uploadError && (
        <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
          {uploadError}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Size</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={block.meta?.size || 'large'}
            onChange={(_, val) => { if (val) onChange({ ...block, meta: { ...block.meta, size: val } }); }}
          >
            <ToggleButton value="small" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1 }}>S</ToggleButton>
            <ToggleButton value="medium" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1 }}>M</ToggleButton>
            <ToggleButton value="large" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1 }}>L</ToggleButton>
            <ToggleButton value="full" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1 }}>Full</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Align</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={block.meta?.align || 'center'}
            onChange={(_, val) => { if (val) onChange({ ...block, meta: { ...block.meta, align: val } }); }}
          >
            <ToggleButton value="left"><FormatAlignLeft sx={{ fontSize: 16 }} /></ToggleButton>
            <ToggleButton value="center"><FormatAlignCenter sx={{ fontSize: 16 }} /></ToggleButton>
            <ToggleButton value="right"><FormatAlignRight sx={{ fontSize: 16 }} /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <TextField
        fullWidth
        placeholder="Caption (optional)"
        value={block.meta?.caption || ''}
        onChange={(e) => onChange({ ...block, meta: { ...block.meta, caption: e.target.value } })}
        sx={{ mb: 1 }}
      />
      {hasPreview && (
        <Box
          sx={{
            mt: 1,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            maxHeight: 300,
            display: 'flex',
            justifyContent: 'center',
            bgcolor: '#f8f9fa',
          }}
        >
          <video
            src={previewUrl}
            controls
            style={{ maxWidth: '100%', maxHeight: 300 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default VideoBlockEditor;
